import {computed} from 'vue';

import type {IPaginationFilters, TId} from '@/assets/core/assets/api/types';
import type {IRequestSuccess} from '@/assets/core/assets/api/types';

import useRequestHook from './requestHook';
import useRequestPaginationHook from './requestPaginationHook';

import type {IQueryObject} from '@/assets/core/assets/helpers/serialize';
import type {IDtoBase} from '@/assets/core/assets/hooks/types';

export type TApiHook<
  TGetShort = unknown,
  TGet = unknown,
  TCreate = unknown,
  TUpdate = unknown,
  TFilters extends IQueryObject = IQueryObject,
> = ReturnType<typeof useAPIHook<TGetShort, TGet, TCreate, TUpdate, TFilters>>;

const useAPIHook = <
  TGetShort = unknown,
  TGet = unknown,
  TCreate = unknown,
  TUpdate = unknown,
  TFilters extends IQueryObject = IQueryObject,
>(
  baseUrl: URL,
  baseFilters?: TFilters,
  basePagination?: Omit<IPaginationFilters, 'page'>,
  dto?: (item: TGetShort) => IDtoBase<TGetShort>,
) => {
  const request = useRequestHook();
  const requestPagination = useRequestPaginationHook<TGetShort, TFilters>(
    baseUrl,
    baseFilters,
    basePagination,
  );

  const getURL = computed(() => baseUrl);

  const getURLById = (id?: TId) => {
    const url = new URL(baseUrl.toString());

    if (id == null) return url;

    if (url.pathname.includes('{id}'))
      url.pathname = url.pathname.replace('{id}', id);
    else if (url.pathname.includes('%7Bid%7D'))
      url.pathname = url.pathname.replace('%7Bid%7D', id);
    else url.pathname += '/' + id.toString();

    return url;
  };

  const clone = () => {
    return useAPIHook<TGetShort, TGet, TCreate, TUpdate, TFilters>(
      baseUrl,
      baseFilters,
      basePagination,
      dto,
    );
  };
  const cloneWithParams = (
    filters?: TFilters,
    pagination?: Omit<IPaginationFilters, 'page'>,
  ) => {
    return useAPIHook<TGetShort, TGet, TCreate, TUpdate, TFilters>(
      baseUrl,
      filters ?? baseFilters,
      pagination ?? basePagination,
      dto,
    );
  };

  const getIsLoading = computed(
    () => request.isLoading.value || requestPagination.isLoading.value,
  );

  const getOneRequest = async (id?: TId): Promise<TGet> => {
    return await request.get(getURLById(id));
  };

  const createOneRequest = async (
    payload: TCreate,
    id?: TId,
  ): Promise<TGet> => {
    return await request.post(getURLById(id), payload);
  };

  const updateOneRequest = async (
    id?: TId,
    payload?: TUpdate,
  ): Promise<TGet> => {
    return await request.patch(getURLById(id), payload);
  };

  const deleteOneRequest = async (id?: TId): Promise<IRequestSuccess> => {
    return await request.delete(getURLById(id));
  };

  return {
    getRequestLastDetail: request.getRequestLastDetail,

    request,

    list: {
      ...requestPagination,

      getPageDto: computed<IDtoBase<TGetShort>[]>(() => {
        return !dto ? [] : requestPagination.getPage.value.map(dto);
      }),
      getPageAllDto: computed<IDtoBase<TGetShort>[]>(() => {
        return !dto ? [] : requestPagination.getPageAll.value.map(dto);
      }),
      getPageWithPrevDto: computed<IDtoBase<TGetShort>[]>(() => {
        return !dto ? [] : requestPagination.getPageWithPrev.value.map(dto);
      }),
    },

    getURL,
    getIsLoading,
    getURLById,

    getOne: getOneRequest,

    createOne: createOneRequest,
    updateOne: updateOneRequest,
    deleteOne: deleteOneRequest,

    clone,
    cloneWithParams,
  };
};

export default useAPIHook;
